"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.roleLabel = exports.ROLE_LABEL = exports.REQUIRED_TEXT_ROLE = exports.MIN_LOGO_FRACTION = exports.EDIT_SCALE_MAX = exports.EDIT_SCALE_MIN = exports.MIN_LEGIBLE_TEXT_PX = exports.ELEMENT_EDIT_OPS = exports.RULE_WIDTHS = exports.EDIT_ANCHORS = exports.TEXT_ROLES = exports.EDITABLE_ROLES = void 0;
/**
 * Targeted edits to one finished design — the vocabulary a refinement request is compiled into.
 *
 * The existing refinement mechanism changes GENERATION INPUTS (the copy, the canvas, the palette
 * emphasis) and re-runs the deterministic pipeline. That covers "make the headline shorter" and
 * cannot express "make the logo bigger", because the logo's frame is not an input — it is something
 * the archetype computes. These edits are the missing half: a small, closed set of operations applied
 * to the composed scene graph, between building it and measuring it.
 *
 * WHY THIS SHAPE. Every field is an enum, a role, a bounded number or verbatim user text — the same
 * discipline the composition contract keeps, and for the same reason: this is filled in by a model,
 * and a schema that admits free-form geometry invites a model to emit coordinates it has no way to
 * check. An edit names WHAT to change and by HOW MUCH in relative terms; where that lands on the
 * canvas is computed here, against the design that actually exists.
 *
 * The safety property is the same one the sparse plan already relies on: an edit that is not in the
 * list cannot happen, because no code path reads anything else. "Anything you don't mention stays
 * exactly as it is" is therefore structural rather than something the prompt has to be trusted about.
 */
/** Roles an edit may name. `background` is the canvas itself and can only be recoloured. */
exports.EDITABLE_ROLES = [
    'headline',
    'subhead',
    'eyebrow',
    'image',
    'logo',
    'accent',
    'panel',
    'scrim',
    'background',
];
/** Text roles — the ones whose content and font size can change. */
exports.TEXT_ROLES = ['headline', 'subhead', 'eyebrow'];
/**
 * Where an element can be moved to.
 *
 * Nine anchors rather than coordinates. "Move the logo to the top right" is a statement about a
 * corner, and a model asked for x/y would be inventing pixels against a canvas it cannot see.
 */
exports.EDIT_ANCHORS = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
/** Widths a decorative rule can be swapped between — the "change" operation's closed set. */
exports.RULE_WIDTHS = ['short', 'wide', 'full'];
exports.ELEMENT_EDIT_OPS = [
    'resize',
    'move',
    'restyle',
    'recolor',
    'setText',
    'remove',
];
/**
 * The smallest type anyone should be asked to read, in px at any canvas size.
 *
 * Matches the floor the layout pass already refuses to shrink past, so "clamped at the minimum
 * legible size" means one number rather than two that can drift.
 */
exports.MIN_LEGIBLE_TEXT_PX = 12;
/**
 * How far a single edit may scale something. A resize is a nudge, not a rebuild.
 *
 * The upper bound is a backstop only — the real ceiling is geometric (the element's slot, and its
 * neighbours), measured against the design and reported when it binds. Without a backstop, "make it
 * huge" would ask for a 40× logo and the clamp message would read as a malfunction.
 */
exports.EDIT_SCALE_MIN = 0.2;
exports.EDIT_SCALE_MAX = 5;
/** A logo below this fraction of the canvas's shorter edge is decoration, not a mark. */
exports.MIN_LOGO_FRACTION = 0.02;
/** Roles that carry the design's message. Removing the last of these leaves nothing to say. */
exports.REQUIRED_TEXT_ROLE = 'headline';
/** Human wording for a role, for messages the person reads. */
exports.ROLE_LABEL = {
    headline: 'headline',
    subhead: 'subhead',
    eyebrow: 'eyebrow',
    image: 'image',
    logo: 'logo',
    accent: 'accent rule',
    panel: 'panel',
    scrim: 'scrim',
    background: 'background',
};
const roleLabel = (role) => exports.ROLE_LABEL[role] ?? role;
exports.roleLabel = roleLabel;
//# sourceMappingURL=design-edits.js.map